import type { BusinessConnectionMst } from '@libs/domain';
import { businessConnectionMst } from '@libs/domain';
import type { BusinessConnectionType } from '@libs/enums';
import { businessConnectionType, eligibleBillingProvider } from '@libs/enums';
import type { MstRangeSearchProps, MstSearchDialogProps, MstSearchProps } from '@libs/templates/mst';
import { MstSearchDialogTmpl } from '@libs/templates/mst';
import type { Characters, Item, PageProps, WithExclusion } from '@ps-smart-cp/ppfw-client';
import {
  MasterRangeSearchField,
  MasterSearch,
  MasterSearchField,
  withRerender,
} from '@ps-smart-cp/ppfw-client';
import { AdditionalSearchBasicInfo, businessConnectionMstColumns } from './components';
import { businessConnectionMstListSearchFieldConfig } from './item-config';
import type { BusinessConnectionMstRegistration } from './models';
import { BusinessConnectionMstListSearch } from './models';
import { fetchMst, listRequest } from './repositories';

function InnerComponent(
  props: MstSearchDialogProps<BusinessConnectionMstRegistration> &
    PageProps & { businessConnectionClass?: BusinessConnectionType },
) {
  return (
    <MstSearchDialogTmpl
      mstName={businessConnectionMst.labelNames.businessConnection}
      columns={businessConnectionMstColumns}
      items={businessConnectionMstListSearchFieldConfig}
      parsers={{
        eligibleBillingProviderCondition: eligibleBillingProvider.toDisplay,
        businessConnectionClass: businessConnectionType.toDisplay,
      }}
      listRequestFunction={listRequest}
      initMst={() => BusinessConnectionMstListSearch.init(props.businessConnectionClass)}
      {...props}
    >
      {AdditionalSearchBasicInfo}
    </MstSearchDialogTmpl>
  );
}

const Dialog = withRerender(InnerComponent, 'BusinessConnectionMstDialog');

function BusinessConnectionMasterSearchField<
  T extends Pick<BusinessConnectionMst['schema'], K>,
  K extends 'businessConnectionMstCode' | 'lentPartyCode' | 'supplierCode',
  I extends Record<K, Item<Characters>>,
>(props: MstSearchProps<T, K, I> & { businessConnectionClass?: BusinessConnectionType }) {
  return (
    <MasterSearchField getMaster={async (code) => fetchMst({ code })} {...props}>
      {(setIsOpen, setMaster, getMst) => (
        <Dialog
          setIsOpen={setIsOpen}
          setMaster={setMaster}
          getMaster={getMst}
          businessConnectionClass={props.businessConnectionClass}
        />
      )}
    </MasterSearchField>
  );
}

function BusinessConnectionMasterSearch<
  T extends Pick<BusinessConnectionMst['schema'], K>,
  K extends 'businessConnectionMstCode' | 'lentPartyCode' | 'supplierCode',
  I extends Record<K, Item<Characters>>,
>(props: MstSearchProps<T, K, I> & { businessConnectionClass?: BusinessConnectionType }) {
  return (
    <MasterSearch
      getMaster={async (code) => fetchMst({ code })}
      itemAttribute={props.items[props.prop].attribute}
      {...props}
    >
      {(setIsOpen, setMaster, getMst) => (
        <Dialog
          setIsOpen={setIsOpen}
          setMaster={setMaster}
          getMaster={getMst}
          businessConnectionClass={props.businessConnectionClass}
        />
      )}
    </MasterSearch>
  );
}

function BusinessConnectionMasterRangeSearch<
  T extends Pick<BusinessConnectionMst['schema'], K>,
  K extends 'businessConnectionMstCodeRange' | 'lentPartySuppCodeRange' | 'supplierSuppCodeRange',
  I extends Record<K, Item<WithExclusion>>,
>(props: MstRangeSearchProps<T, K, I> & { businessConnectionClass?: BusinessConnectionType }) {
  return (
    <MasterRangeSearchField getMaster={async (code) => fetchMst({ code })} {...props}>
      {(setIsOpen, setMaster, getMst) => (
        <Dialog
          setIsOpen={setIsOpen}
          setMaster={setMaster}
          getMaster={getMst}
          businessConnectionClass={props.businessConnectionClass}
        />
      )}
    </MasterRangeSearchField>
  );
}

export {
  BusinessConnectionMasterRangeSearch,
  BusinessConnectionMasterSearch,
  BusinessConnectionMasterSearchField,
};
